import { Alert, Button, Card, Image, Segmented, Space, Statistic, Tag, Typography } from "antd";
import type {
    DataStatisticsVo,
    ProductConsignmentVo,
    ProductStaticDataVo,
    ProductTrendVo,
    SalesRegionVo,
    VisitorRegionVo,
    ReceiveRegionTopVo,
    TrendDataItem,
    PriceSalesDistributionVo,
    PurchaseRankingVo
} from "@zhihaoscm/entity";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import { consignmentService } from "@/api/services/consignmentService";
import { dashboardService } from "@/api/services/dashboardService";
import { COMMODITY_PRICE_TYPE } from "@zhihaoscm/meta";
import {
    BaseItem as ZhBaseItem,
    DateRangePicker as ZhDateRangePicker,
    Grid as ZhGrid,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import AudienceProfilePanel from "./AudienceProfilePanel";

type DetailRecord = Partial<ProductConsignmentVo["productConsignment"]> & {
    mainFile?: { path?: string };
    productLabel?: string[];
    productTypeName?: string;
    supplierEnterprise?: { name?: string };
};

export interface ConsignmentGoodsAnalysisReceiveRegionParams {
    productId: string;
    sortType: number;
    beginTime?: string;
    endTime?: string;
}

export interface ConsignmentGoodsAnalysisDetailPageProps {
    productId?: string;
    loadDetail?: (
        id: string
    ) => Promise<ProductConsignmentVo | { productConsignment?: DetailRecord; mainFile?: { path?: string } }>;
    loadStatic?: (params: { productId: string; beginTime?: string; endTime?: string }) => Promise<ProductStaticDataVo>;
    loadTrend?: (params: {
        productId: string;
        beginTime?: string;
        endTime?: string;
        timeGranularity: number;
    }) => Promise<ProductTrendVo>;
    loadTop?: (id: string) => Promise<unknown>;
    loadCancelTop?: (id: string) => Promise<unknown>;
    loadVisitorRegion?: (params: {
        productId: string;
        areaLevel: number;
        beginTime?: string;
        endTime?: string;
    }) => Promise<VisitorRegionVo[]>;
    loadSalesRegion?: (params: {
        productId: string;
        areaLevel: number;
        beginTime?: string;
        endTime?: string;
    }) => Promise<SalesRegionVo[]>;
    loadReceiveRegion?: (params: ConsignmentGoodsAnalysisReceiveRegionParams) => Promise<ReceiveRegionTopVo[]>;
    loadAllTime?: () => Promise<{ beginTime: string; endTime: string }>;
    loadPurchaseRanking?: (params: {
        productId: string;
        beginTime?: string;
        endTime?: string;
        sortType: number;
    }) => Promise<PurchaseRankingVo[]>;
    top?: (id: string) => Promise<unknown>;
    cancelTop?: (id: string) => Promise<unknown>;
    permissions?: string[];
}

/** 复用 Vue 商品详情接口，保持商品 ID 位于 /vo/{id} 路径。 */
export async function loadConsignmentGoodsAnalysisDetail(id: string) {
    return consignmentService.getDetail(id);
}

/** 复用 Vue 商品详情静态统计接口。 */
export async function loadConsignmentGoodsAnalysisStatic(params: {
    productId: string;
    beginTime?: string;
    endTime?: string;
}) {
    return dashboardService.getProductAnalysisStaticData(params);
}

/** 复用 Vue 商品详情趋势接口，并保持时间粒度参数名。 */
export async function loadConsignmentGoodsAnalysisTrend(params: {
    productId: string;
    beginTime?: string;
    endTime?: string;
    timeGranularity: number;
}) {
    return dashboardService.getProductAnalysisTrendData(params);
}

/** 复用 Vue 商品详情受众画像的访客地域接口。 */
export async function loadConsignmentGoodsAnalysisVisitorRegion(params: {
    productId: string;
    areaLevel: number;
    beginTime?: string;
    endTime?: string;
}) {
    return dashboardService.getVisitorRegion(params);
}

/** 复用 Vue 商品详情受众画像的销售地域接口。 */
export async function loadConsignmentGoodsAnalysisSalesRegion(params: {
    productId: string;
    areaLevel: number;
    beginTime?: string;
    endTime?: string;
}) {
    return dashboardService.getSalesRegion(params);
}

/** 复用 Vue 商品详情受众画像的收货地排名接口。 */
export async function loadConsignmentGoodsAnalysisReceiveRegion(params: ConsignmentGoodsAnalysisReceiveRegionParams) {
    return dashboardService.getReceiveRegionTop5(params);
}

/** 复用 Vue 商品详情“全部”按钮的完整历史时间范围接口。 */
export async function loadConsignmentGoodsAnalysisAllTime() {
    return dashboardService.getAllTime();
}

/** 复用 Vue 商品详情采购排名 TOP5 接口，保持排序参数和时间范围契约。 */
export async function loadConsignmentGoodsAnalysisPurchaseRanking(params: {
    productId: string;
    beginTime?: string;
    endTime?: string;
    sortType: number;
}) {
    return dashboardService.getProductAnalysisPurchaseTop5(params);
}

const formatDateTime = (value: string) => `${value} 00:00:00`;

// 趋势表沿用 Vue 字段名，统一通过 zhihao-ui-react 列模型展示时间、金额、销量和转化率。
const trendColumns: TableColumnProps<TrendDataItem>[] = [
    { label: "时间", prop: "dateStr" },
    { label: "销售额", prop: "totalSalesAmount" },
    { label: "销量", prop: "totalSalesTon" },
    { label: "订单数", prop: "orderCount" },
    { label: "转化率", prop: "conversionRate" }
];

/** 对齐旧 Vue 价格销量分布的数值展示，空值只回退为占位符。 */
const formatAnalysisNumber = (value: number | null | undefined) =>
    value === null || value === undefined ? "--" : Number(value).toLocaleString("en-US", { maximumFractionDigits: 2 });

const distributionColumns: TableColumnProps<PriceSalesDistributionVo["distributionList"][number]>[] = [
    { label: "价格区间", prop: "priceRange" },
    { label: "销量(吨)", prop: "salesTon", render: ({ row }) => formatAnalysisNumber(row.salesTon) },
    { label: "销量占比(%)", prop: "proportion", render: ({ row }) => formatAnalysisNumber(row.proportion) }
];

const purchaseRankingColumns: TableColumnProps<PurchaseRankingVo>[] = [
    { label: "排名", prop: "rank", render: ({ rowIndex }) => rowIndex + 1 },
    { label: "用户名称", prop: "purchaserName" },
    { label: "订单量", prop: "orderCount", render: ({ row }) => formatAnalysisNumber(row.orderCount) },
    { label: "下单吨数(吨)", prop: "purchaseTon", render: ({ row }) => formatAnalysisNumber(row.purchaseTon) }
];

/** 商品运营分析详情，保留 Vue 的数据概览、受众画像和置顶操作。 */
export function ConsignmentGoodsAnalysisDetailPage({
    productId,
    loadDetail = loadConsignmentGoodsAnalysisDetail,
    loadStatic = loadConsignmentGoodsAnalysisStatic,
    loadTrend = loadConsignmentGoodsAnalysisTrend,
    loadVisitorRegion = loadConsignmentGoodsAnalysisVisitorRegion,
    loadSalesRegion = loadConsignmentGoodsAnalysisSalesRegion,
    loadReceiveRegion = loadConsignmentGoodsAnalysisReceiveRegion,
    loadAllTime = loadConsignmentGoodsAnalysisAllTime,
    loadPurchaseRanking = loadConsignmentGoodsAnalysisPurchaseRanking,
    top = id => consignmentService.top(id),
    cancelTop = id => consignmentService.cancelTop(id),
    permissions
}: ConsignmentGoodsAnalysisDetailPageProps) {
    const routeParams = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const resolvedId = productId || routeParams.id || new URLSearchParams(location.search).get("id") || "";
    const [detail, setDetail] = useState<DetailRecord>();
    const [staticData, setStaticData] = useState<Partial<ProductStaticDataVo>>({});
    const [trend, setTrend] = useState<ProductTrendVo["trendDataList"]>([]);
    const [purchaseRanking, setPurchaseRanking] = useState<PurchaseRankingVo[]>([]);
    const [purchaseSortType, setPurchaseSortType] = useState(2);
    const [tab, setTab] = useState<"overview" | "audience">("overview");
    const [timeGranularity, setTimeGranularity] = useState(1);
    const [startDate, setStartDate] = useState(() => dayjs().subtract(1, "month").add(1, "day").format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(() => dayjs().format("YYYY-MM-DD"));
    const [errorMessage, setErrorMessage] = useState("");
    const [operating, setOperating] = useState(false);
    // 显式传入的空权限列表表示当前用户没有商品运营权限；未传入时继续交由路由权限门禁处理。
    const canOperate = permissions === undefined || permissions.includes("ROLE_SAND_PRODUCT_BI_R");
    const statistics = staticData.dataStatistics as Partial<DataStatisticsVo> | undefined;
    const priceSalesDistribution = staticData.priceSalesDistribution as Partial<PriceSalesDistributionVo> | undefined;
    const product = detail;

    useEffect(() => {
        if (!resolvedId) return;
        let disposed = false;
        void loadDetail(resolvedId)
            .then(result => {
                if (disposed) return;
                const record =
                    (result as { productConsignment?: DetailRecord; mainFile?: { path?: string } })
                        .productConsignment || result;
                const mainFile = (result as { mainFile?: { path?: string } }).mainFile;
                setDetail({ ...record, mainFile: record.mainFile || mainFile });
                setErrorMessage("");
            })
            .catch(() => {
                if (!disposed) setErrorMessage("商品分析详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDetail, resolvedId]);

    useEffect(() => {
        if (!resolvedId) return;
        let disposed = false;
        const params = { productId: resolvedId, beginTime: formatDateTime(startDate), endTime: `${endDate} 23:59:59` };
        void Promise.all([loadStatic(params), loadTrend({ ...params, timeGranularity })])
            .then(([summary, trendResult]) => {
                if (disposed) return;
                setStaticData(summary || {});
                setTrend(trendResult?.trendDataList || []);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("商品分析统计加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [endDate, loadStatic, loadTrend, resolvedId, startDate, timeGranularity]);

    // 采购排名接口独立处理，避免该非核心卡片失败时清空旧 Vue 的统计与趋势数据。
    useEffect(() => {
        if (!resolvedId) return;
        let disposed = false;
        const params = { productId: resolvedId, beginTime: formatDateTime(startDate), endTime: `${endDate} 23:59:59` };
        void loadPurchaseRanking({ ...params, sortType: purchaseSortType })
            .then(result => {
                if (!disposed) setPurchaseRanking(result || []);
            })
            .catch(() => {
                if (!disposed) setPurchaseRanking([]);
            });
        return () => {
            disposed = true;
        };
    }, [endDate, loadPurchaseRanking, purchaseSortType, resolvedId, startDate]);

    const dateLabel = useMemo(() => `${startDate} 至 ${endDate}`, [endDate, startDate]);

    const handleTop = async () => {
        if (!resolvedId || !canOperate || operating) return;
        setOperating(true);
        try {
            if (product?.isTop) await cancelTop(resolvedId);
            else await top(resolvedId);
            setDetail(current => (current ? { ...current, isTop: current.isTop ? 0 : 1 } : current));
        } catch {
            setErrorMessage("商品置顶操作失败");
        } finally {
            setOperating(false);
        }
    };

    /** 点击“全部”时沿用 Vue 接口返回的历史起止日期，而不是伪造固定近一年范围。 */
    const handleSelectAllTime = async () => {
        try {
            const allTime = await loadAllTime();
            setStartDate(dayjs(allTime.beginTime).format("YYYY-MM-DD"));
            setEndDate(dayjs(allTime.endTime).format("YYYY-MM-DD"));
        } catch {
            setErrorMessage("商品分析时间范围加载失败");
        }
    };

    const soldTon = Number(product?.orderedTon || 0);
    const totalTon = Number(product?.initInventoryQuantity || 0);
    const soldPercentage = totalTon > 0 ? Math.ceil((soldTon / totalTon) * 100) : 0;

    return (
        <section aria-label="consignment-goods-analysis-detail-page" className="flex min-h-full flex-col gap-3">
            {errorMessage ? <Alert role="alert" message={errorMessage} type="error" /> : null}
            <Card>
                <Space className="w-full" align="center" style={{ justifyContent: "space-between" }}>
                    <Typography.Title className="mb-0 mt-0" level={4}>
                        商品运营分析详情
                    </Typography.Title>
                    <Space>
                        <Tag color={product?.state === 1 ? "green" : "default"}>
                            {product?.state === 1 ? "上架" : "下架"}
                        </Tag>
                        <Tag>{product?.isTop ? "已置顶" : "未置顶"}</Tag>
                    </Space>
                </Space>
            </Card>
            <Card>
                <Space align="start" className="w-full">
                    {product?.mainFile?.path ? (
                        <Image width={160} height={160} src={product.mainFile.path} alt="商品主图" />
                    ) : null}
                    <Space direction="vertical">
                        <Typography.Title level={4} className="mb-0 mt-0">
                            {product?.title || "--"}
                        </Typography.Title>
                        <Space>
                            {(product?.productLabel || []).map(label => (
                                <Tag key={label}>{label}</Tag>
                            ))}
                        </Space>
                        {/* 基础信息沿用 Vue 字段顺序，使用 zhihao-ui-react 保持管理后台详情项样式一致。 */}
                        <ZhGrid cols={1} gap={[8, 8]}>
                            <ZhBaseItem label="品类" value={product?.productTypeName || "--"} />
                            <ZhBaseItem label="规格" value={product?.productTypeSpec || "--"} />
                            <ZhBaseItem label="供应商" value={product?.supplierEnterprise?.name || "--"} />
                            <ZhBaseItem
                                label="当前单价"
                                value={
                                    <span>
                                        {formatAnalysisNumber(product?.unitPrice)} 元/吨
                                        {product?.priceType
                                            ? `（${COMMODITY_PRICE_TYPE.getIntroByCode(product.priceType)}）`
                                            : ""}
                                    </span>
                                }
                            />
                            <ZhBaseItem
                                label="售卖进度"
                                value={
                                    <span aria-label={`商品售卖进度 已售${soldPercentage}%`}>
                                        已售{soldPercentage}%（{formatAnalysisNumber(soldTon)} 吨 /{" "}
                                        {formatAnalysisNumber(totalTon)} 吨）
                                    </span>
                                }
                            />
                            <ZhBaseItem
                                label="商品编号"
                                value={
                                    <Link to={`/productManagement/consignment/detail?id=${product?.id || resolvedId}`}>
                                        {product?.id || resolvedId}
                                    </Link>
                                }
                            />
                        </ZhGrid>
                    </Space>
                    <Space direction="vertical" style={{ marginLeft: "auto" }}>
                        {canOperate ? (
                            <Button type="primary" loading={operating} onClick={handleTop}>
                                {product?.isTop ? "取消置顶" : "置顶该商品"}
                            </Button>
                        ) : (
                            <Typography.Text type="secondary">暂无置顶操作权限</Typography.Text>
                        )}
                        <Typography.Text type="secondary">
                            {product?.isTop
                                ? "取消置顶后会从商品运营分析列表移除置顶"
                                : "置顶后会展示在商品运营分析列表顶部"}
                        </Typography.Text>
                    </Space>
                </Space>
            </Card>
            <Card>
                <Segmented
                    value={tab}
                    onChange={value => setTab(value as "overview" | "audience")}
                    options={[
                        { label: "数据概览", value: "overview" },
                        { label: "受众画像", value: "audience" }
                    ]}
                />
                <Space className="mt-4 w-full" align="center" style={{ justifyContent: "space-between" }}>
                    <Typography.Title level={5} className="mb-0 mt-0">
                        {tab === "overview" ? "数据概览" : "受众画像"}
                    </Typography.Title>
                    {tab === "overview" ? (
                        <Typography.Text>商品收藏人数：{statistics?.collectUserCount ?? 0}人</Typography.Text>
                    ) : null}
                </Space>
                <Space className="my-4" wrap>
                    <Button onClick={() => void handleSelectAllTime()}>全部</Button>
                    <Segmented
                        value={timeGranularity}
                        onChange={value => setTimeGranularity(Number(value))}
                        options={[
                            { label: "日", value: 1 },
                            { label: "周", value: 2 },
                            { label: "月", value: 3 },
                            { label: "年", value: 4 }
                        ]}
                    />
                    <ZhDateRangePicker
                        aria-label="日期范围"
                        format="YYYY-MM-DD"
                        startPlaceholder="开始日期"
                        endPlaceholder="结束日期"
                        type="daterange"
                        value={[startDate, endDate]}
                        valueFormat="YYYY-MM-DD"
                        onChange={(_value, formattedValue) => {
                            if (!formattedValue) return;
                            // 保持 Vue 日期范围组件的双值更新语义，变更后重新加载统计接口。
                            setStartDate(formattedValue[0] || startDate);
                            setEndDate(formattedValue[1] || endDate);
                        }}
                    />
                    <Typography.Text type="secondary">{dateLabel}</Typography.Text>
                </Space>
                {tab === "overview" ? (
                    <>
                        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                            <Statistic title="总销售额(元)" value={statistics?.totalSalesAmount ?? 0} />
                            <Statistic title="结算金额(元)" value={statistics?.settlementAmount ?? 0} />
                            <Statistic title="退款金额(元)" value={statistics?.refundAmount ?? 0} />
                            <Statistic title="总销量(吨)" value={statistics?.totalSalesVolume ?? 0} />
                            <Statistic title="实际结算吨数(吨)" value={statistics?.actualSettledTons ?? 0} />
                            <Statistic title="产生订单" value={statistics?.generateOrderCount ?? 0} />
                            <Statistic title="客单价(元/吨)" value={statistics?.averagePrice ?? 0} />
                            <Statistic title="商品访问人数" value={statistics?.productVisitorCount ?? 0} />
                            <Statistic title="商品访问次数" value={statistics?.productVisitCount ?? 0} />
                            <Statistic title="平均用户停留时长" value={statistics?.avgUserStayTime ?? 0} suffix="秒" />
                            <Statistic
                                title="平均单次停留时长"
                                value={statistics?.avgSingleStayTime ?? 0}
                                suffix="秒"
                            />
                        </div>
                        <Typography.Title level={5}>趋势分析</Typography.Title>
                        <ZhTable<TrendDataItem>
                            rowKey="dateStr"
                            data={trend}
                            pagination={false}
                            emptyText="暂无数据"
                            columns={trendColumns}
                        />
                        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                            <section aria-label="价格销量分布">
                                <Typography.Title level={5}>价格销量分布</Typography.Title>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        当前指数价格: {formatAnalysisNumber(priceSalesDistribution?.currentIndexPrice)}{" "}
                                        元/吨
                                    </div>
                                    <div>总销量: {formatAnalysisNumber(priceSalesDistribution?.totalSalesTon)} 吨</div>
                                </div>
                                <table aria-label="价格销量分布表" className="mt-3 w-full text-left text-sm">
                                    <thead>
                                        <tr>
                                            {distributionColumns.map(column => (
                                                <th key={String(column.prop)}>{column.label}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(priceSalesDistribution?.distributionList || []).map(row => (
                                            <tr key={row.priceRange}>
                                                <td>{row.priceRange}</td>
                                                <td>{formatAnalysisNumber(row.salesTon)}</td>
                                                <td>{formatAnalysisNumber(row.proportion)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </section>
                            <section aria-label="采购排名TOP5">
                                <Space className="w-full" align="center" style={{ justifyContent: "space-between" }}>
                                    <Typography.Title level={5} className="mb-0">
                                        采购排名TOP5
                                    </Typography.Title>
                                    <Segmented
                                        value={purchaseSortType}
                                        onChange={value => setPurchaseSortType(Number(value))}
                                        options={[
                                            { label: "订单优先", value: 1 },
                                            { label: "销量优先", value: 2 }
                                        ]}
                                    />
                                </Space>
                                <table aria-label="采购排名TOP5" className="mt-3 w-full text-left text-sm">
                                    <thead>
                                        <tr>
                                            {purchaseRankingColumns.map(column => (
                                                <th key={String(column.prop)}>{column.label}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {purchaseRanking.map((row, rowIndex) => (
                                            <tr key={row.customerId}>
                                                <td>{rowIndex + 1}</td>
                                                <td>{row.purchaserName}</td>
                                                <td>{formatAnalysisNumber(row.orderCount)}</td>
                                                <td>{formatAnalysisNumber(row.purchaseTon)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </section>
                        </div>
                    </>
                ) : (
                    <AudienceProfilePanel
                        productId={resolvedId}
                        startDate={startDate}
                        endDate={endDate}
                        loadVisitorRegion={loadVisitorRegion}
                        loadSalesRegion={loadSalesRegion}
                        loadReceiveRegion={loadReceiveRegion}
                    />
                )}
            </Card>
            <Button onClick={() => navigate(-1)}>返回</Button>
        </section>
    );
}

export default ConsignmentGoodsAnalysisDetailPage;
